import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircleIcon,
  ArrowClockwiseIcon,
  BankIcon,
} from "@phosphor-icons/react";

import api from "../../lib/api";

/**
 * Stripe redirect target after Checkout success.
 * URL: /billing/checkout-complete?subscription_id=...&session_id=...
 *
 * The webhook fires asynchronously — we poll the subscription's billing info
 * for up to ~30s waiting for billing_mode=stripe to land.
 *
 * Phase 7.5 — ACH detection: if the polled billing info comes back with
 * bank_account_verification_status === "pending_verification", render the
 * "Payment processing" screen instead of the "You're all set" success screen.
 * This is the cleanest signal — no reliance on post-redirect URL params.
 */
export default function CheckoutComplete() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const subId = params.get("subscription_id");
  // waiting | linked | linked_ach_pending | timeout
  const [status, setStatus] = useState("waiting");

  useEffect(() => {
    if (!subId) {
      setStatus("timeout");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      while (!cancelled && attempts < 15) {
        attempts += 1;
        try {
          const { data } = await api.get(`/subscriptions/${subId}/billing`);
          if (data.billing_mode === "stripe") {
            if (!cancelled) {
              setStatus(
                data.bank_account_verification_status === "pending_verification"
                  ? "linked_ach_pending"
                  : "linked"
              );
            }
            return;
          }
        } catch {
          /* swallow */
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [subId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-md border border-slate-200 shadow-card p-8 text-center">
        {status === "waiting" && (
          <div data-testid="checkout-waiting">
            <ArrowClockwiseIcon
              size={36}
              weight="duotone"
              className="text-amber-600 animate-spin mx-auto mb-4"
            />
            <div className="font-heading text-lg font-semibold text-slate-900">
              Finalizing your subscription
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Just a moment — Stripe is confirming the payment.
            </p>
          </div>
        )}
        {status === "linked" && (
          <div data-testid="checkout-success">
            <CheckCircleIcon
              size={48}
              weight="duotone"
              className="text-emerald-600 mx-auto mb-4"
            />
            <div className="font-heading text-lg font-semibold text-slate-900">
              You're all set
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Automatic billing is now active. We'll renew this plan on each
              cycle and send a receipt to your email.
            </p>
            <button
              type="button"
              onClick={() => nav(`/subscriptions/${subId}`)}
              data-testid="checkout-back-button"
              className="mt-6 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Back to subscription
            </button>
          </div>
        )}
        {status === "linked_ach_pending" && (
          <div data-testid="checkout-ach-pending">
            <BankIcon
              size={48}
              weight="duotone"
              className="text-amber-600 mx-auto mb-4"
            />
            <div className="font-heading text-lg font-semibold text-slate-900">
              Payment processing
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Your bank transfer is being verified. This can take up to{" "}
              <strong>4 business days</strong>. We'll email you once the first
              payment clears — the subscription will activate automatically
              at that point.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              No action required from you right now.
            </p>
            <button
              type="button"
              onClick={() => nav(`/subscriptions/${subId}`)}
              data-testid="checkout-ach-back-button"
              className="mt-6 inline-flex items-center rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              View subscription
            </button>
          </div>
        )}
        {status === "timeout" && (
          <div data-testid="checkout-timeout">
            <div className="font-heading text-lg font-semibold text-slate-900">
              Almost there…
            </div>
            <p className="text-sm text-slate-600 mt-2">
              Stripe is still finalizing — this can sometimes take a minute.
              The subscription will update automatically once the webhook
              arrives.
            </p>
            <button
              type="button"
              onClick={() => nav(`/subscriptions/${subId}`)}
              className="mt-6 inline-flex items-center rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              View subscription
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
